import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Image as ImageIcon, X, Loader2, Info } from "lucide-react";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { TruncatedText } from "@/components/ui/truncated-text";
import { toast } from "sonner";
import { useUploadPastTest } from "@/resources/ab-test/ab-test.api";
import type { UploadPastTestResponse } from "@/types/ab-tests";

const ACCEPTED_EXTENSIONS = /\.(png|jpe?g|webp|gif|csv|pdf)$/i;
const ACCEPTED_INPUT = ".png,.jpg,.jpeg,.webp,.gif,.csv,.pdf,image/*,text/csv,application/pdf";

interface UploadPastTestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number;
  brandId: number;
}

export function UploadPastTestModal({ open, onOpenChange, productId, brandId }: UploadPastTestModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState<UploadPastTestResponse | null>(null);
  const uploadPastTest = useUploadPastTest();
  const parsing = uploadPastTest.isPending;

  const reset = () => {
    setFile(null);
    setDragging(false);
    setResult(null);
  };

  const handleClose = () => {
    if (parsing) return;
    reset();
    onOpenChange(false);
  };

  const accept = (f: File) => {
    if (!ACCEPTED_EXTENSIONS.test(f.name)) {
      toast.error("Unsupported file", { description: "Upload a screenshot (PNG/JPG), PDF, or CSV." });
      return;
    }
    setFile(f);
  };

  const handleParse = () => {
    if (!file) return;
    uploadPastTest.mutate(
      { productId, brandId, file },
      {
        onSuccess: (data) => {
          const response = data as UploadPastTestResponse;
          toast.success("Signal AI parsed your test", {
            description: data.hasParsedMetrics
              ? `Test created with parsed metrics. Outcome: ${data.outcome}`
              : "Test created. Add metrics manually if needed.",
          });
          // FR-3 (AUT-357 WEB-003): a non-empty extraction_summary gets a result banner instead of
          // an immediate close, so the user sees what Signal AI could/couldn't read. Null/absent
          // keeps the prior toast-and-close behavior.
          if (response.extractionSummary && response.extractionSummary.trim().length > 0) {
            setFile(null);
            setResult(response);
          } else {
            reset();
            onOpenChange(false);
          }
        },
        onError: () => {
          toast.error("Upload failed", { description: "Could not import the past test. Try again." });
        },
      },
    );
  };

  const isImage = file && /\.(png|jpe?g|webp|gif)$/i.test(file.name);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-card">
        <DialogHeader>
          <DialogTitle>Upload a past A/B test</DialogTitle>
          <DialogDescription>
            Drop a screenshot, PDF, or CSV of your past test. Signal AI will parse the configuration,
            results, and any signals it can identify — you can edit anything that's missing or wrong.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {result?.extractionSummary && (
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
              <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">{result.extractionSummary}</p>
            </div>
          )}

          {!result && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) accept(f);
            }}
            onClick={() => !file && inputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
            } ${!file ? "cursor-pointer" : ""}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_INPUT}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) accept(f);
                e.target.value = "";
              }}
            />
            {!file ? (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">Drag and drop or click to upload</p>
                <p className="text-xs text-muted-foreground">Screenshot (PNG, JPG), PDF, or CSV</p>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 min-w-0 w-full">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {isImage ? (
                    <ImageIcon className="h-5 w-5 text-primary shrink-0" />
                  ) : (
                    <FileText className="h-5 w-5 text-primary shrink-0" />
                  )}
                  <span className="text-sm font-medium min-w-0">
                    <TruncatedText>{file.name}</TruncatedText>
                  </span>
                </div>
                {!parsing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    aria-label="Remove file"
                    className="shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          )}

          {parsing && (
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
              <AISparkles className="h-4 w-4" />
              <span className="text-sm font-medium text-foreground">Signal AI is parsing your test…</span>
            </div>
          )}
        </div>

        <DialogFooter>
          {result ? (
            <Button onClick={() => { reset(); onOpenChange(false); }}>Done</Button>
          ) : (
          <>
          <Button variant="outline" onClick={handleClose} disabled={parsing}>Cancel</Button>
          <Button onClick={handleParse} disabled={!file || parsing} className="gap-1.5">
            {parsing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Parsing…
              </>
            ) : (
              <>
                <AISparkles className="h-3.5 w-3.5" />
                Parse with Signal AI
              </>
            )}
          </Button>
          </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
